// Inverts Loom's LEGACY_SHIFT_EVENT_TYPE_MAP (src/ingest/transform/shift.ts). The pairing is not
// the one the names suggest: legacy clockInConfirmation is Loom's CLOCK_IN, while Loom's
// CLOCK_IN_NOTIFICATION is the uppercase reminder type no timeline tile or filter reads.
const CAREOS_EVENT_TYPE_BY_LOOM_TYPE = {
  SHIFT_NOTIFICATION: 'shiftConfirmation',
  CLOCK_IN_NOTIFICATION: 'CLOCK_IN_CONFIRMATION',
  CLOCK_OUT_NOTIFICATION: 'CLOCK_OUT_CONFIRMATION',
  CLOCK_IN: 'clockInConfirmation',
  CLOCK_OUT: 'clockOutConfirmation',
  REQUEST_CANCEL: 'requestCancel',
  REQUEST_RESCHEDULE: 'requestReschedule',
  MISSED_SHIFT_NOTIFICATION: 'shiftConfirmationMissed',
  MISSED_CLOCK_IN_NOTIFICATION: 'clockInConfirmationMissed',
  MISSED_CLOCK_OUT_NOTIFICATION: 'clockOutConfirmationMissed',
};

const personName = (person) => {
  return [person?.firstName, person?.lastName].filter(Boolean).join(' ');
};

// Loom stores the coordinates as numeric, which the driver hands back as strings; leaflet needs
// numbers, and the tile prints "no location" on undefined rather than on a pair of nulls.
const locationFrom = (event) => {
  if (event.latitude == null || event.longitude == null) {
    return undefined;
  }

  return {latitude: Number(event.latitude), longitude: Number(event.longitude)};
};

// shift_event keeps no dedup key, so a corrected clock lands as a second row and overwrites the
// shift's actual time. That column is therefore the admin-corrected value legacy calls
// "suggested", while each event row holds the time of its own clock.
const dataFor = (careosType, event, shift) => {
  switch (careosType) {
    case 'requestCancel':
      return {reason: event.reason || undefined};
    case 'requestReschedule':
      return {rescheduleReason: event.reason || undefined};
    case 'clockInConfirmation':
      return {
        location: locationFrom(event),
        actualClockinTimeFormatted: event.createdAt,
        suggestedClockinTimeFormatted: shift.actualStartTime || undefined,
      };
    case 'clockOutConfirmation':
      return {
        location: locationFrom(event),
        actualClockoutTimeFormatted: event.createdAt,
        suggestedClockoutTimeFormatted: shift.actualEndTime || undefined,
      };
    default:
      return {};
  }
};

const adaptCaregiverUpdates = (shift) => {
  return (shift.events || []).reduce((acc, event) => {
    const type = CAREOS_EVENT_TYPE_BY_LOOM_TYPE[event.type];
    if (!type) return acc;

    return [...acc, {type, createdAt: event.createdAt, data: dataFor(type, event, shift)}];
  }, []);
};

export const adaptLoomShift = (shift) => {
  const cancelled = shift.status === 'CANCELLED';

  return {
    eventRn: shift.legacyEventRn,
    clientRn: shift.client?.legacyClientRn,
    // An occurrence RN is "<seriesRn>:<date>"; a fixed event has no suffix and no series.
    recurrenceRn: /:\d{4}-\d{2}-\d{2}$/.test(shift.legacyEventRn || '') ?
      shift.legacyEventRn.replace(/:\d{4}-\d{2}-\d{2}$/, '') :
      undefined,
    status: cancelled ? 'DEACTIVATED' : 'ACTIVE',
    // Only the live-in value is mapped, for the tile styling. FIXED and RECURRENCE_EVENT are what
    // NotesTile gates its save buttons on, and this page is read-only.
    type: shift.type === 'LIVE_IN' ? 'LIVEIN_EVENT' : undefined,
    shiftType: shift.label || 'REGULAR_CARE',
    info: {
      // Legacy titles a schedule event with the client's name, and every one of the 12402 dev
      // shifts has one, so there is nothing to fall back to.
      title: personName(shift.client?.person),
      description: {blob: shift.notes || '', editorType: 'TEXT'},
    },
    eventData: {
      startTime: shift.scheduledStartTime,
      endTime: shift.scheduledEndTime,
      // Loom keeps the instant but not the zone it was booked in. Carve-out on CQ-417.
      timezone: undefined,
    },
    caregiverData: {
      caregiverRn: shift.caregiver?.legacyCaregiverRn,
      caregiverDetail: {
        caregiverRn: shift.caregiver?.legacyCaregiverRn,
        caregiverName: personName(shift.caregiver?.person),
      },
    },
    clientData: {
      clientDetail: {
        clientRn: shift.client?.legacyClientRn,
        clientName: personName(shift.client?.person),
      },
    },
    // Loom collapses CANCELLED, DELETED and RESCHEDULED, so the action is unrecoverable.
    deactivatedData: cancelled ?
      {description: {blob: shift.cancellationReason || '', editorType: 'MARKDOWN'}} :
      undefined,
    breaks: {
      breakCount: shift.breakCount ?? 0,
      breakDuration: shift.breakDuration ?? 0,
    },
    caregiverUpdates: adaptCaregiverUpdates(shift),
    createdAt: shift.createdAt,
  };
};

export const adaptLoomShiftRange = (response) => {
  return {events: (response?.shifts || []).map(adaptLoomShift)};
};

// Loom holds no client profile image, so the avatar falls back to initials. Only the three
// fields UserRow renders are mapped; CQ-406's client adapter is for the list card, not this.
export const adaptLoomClientRoster = (response) => ({
  list: (response?.clients || [])
      .filter((client) => client?.legacyClientRn)
      .map((client) => ({
        clientRn: client.legacyClientRn,
        clientName: personName(client.person),
      })),
  lastEvaluatedKey: response?.nextCursor || null,
});

export const toLoomClientRosterQuery = (filters, sort, cursor) => {
  const statuses = (filters || [])
      .filter((filter) => filter.key === 'status')
      .flatMap((filter) => (Array.isArray(filter.value) ? filter.value : [filter.value]))
      .filter(Boolean);

  return {
    limit: 100,
    cursor,
    sortDirection: sort?.sortDirection?.toLowerCase() || 'desc',
    status: statuses.length ? [...new Set(statuses)].join(',') : undefined,
  };
};

export const toLoomShiftRangeQuery = (rnField, rn, start, end, includeDeactivated) => {
  return {
    [rnField]: rn,
    start,
    end,
    includeCancelled: includeDeactivated ? 'true' : 'false',
  };
};
